/**
 * @fileoverview Onboarding route for new users completing their profile setup
 * @module routes/onboarding
 */

import type { ReactElement } from 'react';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEnhancedAuth } from '@/lib/auth';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { supabase } from '@/lib/supabase';
import { useUserProfile } from '@/features/auth/hooks/useUserProfile';
import { calculateCurrentStep } from '@/features/onboarding/utils/stepCalculator';
import { UserTypeSelection } from '@/features/onboarding/components/UserTypeSelection';
import { XConnectionStep } from '@/features/onboarding/components/XConnectionStep';
import { KOLProfileForm } from '@/features/onboarding/components/KOLProfileForm';
import { ProjectCreatorForm } from '@/features/onboarding/components/ProjectCreatorForm';
import { OnboardingSuccess } from '@/features/onboarding/components/OnboardingSuccess';
import { XLinkingEdgeCaseDialog, type XLinkingEdgeCaseData } from '@/features/onboarding/components/XLinkingEdgeCaseDialog';
import type { Profile, KOLProfileFormData } from '@/features/onboarding/types/onboarding';
import type { ProjectTicketFormData } from '@/features/onboarding/schemas/projectTicketSchema';
import { getStepSearchParams, getPostOnboardingRedirect } from '@/features/onboarding/utils/stepCalculator';
import { BackgroundShape } from '@/components/ui/BackgroundShape';
import {
  getKolManagerByRefCode,
  getStoredManagerReferral,
  clearManagerReferral,
  type PublicKolManager,
} from '@/features/kol-managers';
import { useToast } from '@/hooks/use-toast';

interface OnboardingSearchParams {
  step?: string;
  completed?: string;
  x_edge_case?: boolean;
  xConflict?: boolean;
  xError?: boolean;
}

/**
 * Onboarding page component.
 *
 * ## Simplified Onboarding Flow
 *
 * Manages a streamlined onboarding process that allows immediate dashboard access.
 *
 * ### Flow:
 *
 * #### All Users (1 step):
 * 1. User type selection → Mark onboarding complete → Redirect to Dashboard
 *
 * Additional profile information (X verification, detailed forms) can be completed
 * later from the dashboard settings or profile page.
 *
 * ### Design Philosophy:
 * - Minimize friction to dashboard access
 * - Allow users to explore the platform immediately
 * - Collect additional information progressively as needed
 *
 * ### Backward Compatibility:
 * - X verification and profile forms are still available via direct routes
 * - Can be accessed for users who want to complete their profile immediately
 * - Form submission handlers remain unchanged
 *
 * @component
 */
function OnboardingPage(): ReactElement {
  const navigate = useNavigate();
  const search = useSearch({ from: '/onboarding' }) as OnboardingSearchParams;
  const { user, profile, isAuthenticated, loading, getTwitterHandle, getGoogleEmail, signOut, refreshProfile } = useEnhancedAuth();
  const { user: dynamicUser } = useDynamicContext();
  const { updateProfile } = useUserProfile();
  const { toast } = useToast();

  // Get the effective user ID (from Dynamic or mock user)
  const effectiveUserId = user?.id || dynamicUser?.userId;

  // State for UI components
  const [showEdgeCaseDialog, setShowEdgeCaseDialog] = useState(false);
  const [edgeCaseData, setEdgeCaseData] = useState<XLinkingEdgeCaseData | null>(null);
  const [isRefreshingProfile, setIsRefreshingProfile] = useState(false);

  // State for manager referral (KOL signup via manager ref link)
  const [referringManager, setReferringManager] = useState<PublicKolManager | null>(null);
  const [managerRefCode, setManagerRefCode] = useState<string | null>(null);

  // State for preselected user type (from landing page CTA buttons or manager referral)
  const [preselectedUserType, setPreselectedUserType] = useState<Profile['user_type'] | undefined>(undefined);

  // Check localStorage for onboarding intent (from landing page buttons) on mount
  useEffect(() => {
    const onboardingIntent = localStorage.getItem('onboarding_intent');
    if (onboardingIntent) {
      // Map intent to user type
      const intentToUserType: Record<string, Profile['user_type']> = {
        'project_creator': 'project_creator',
        'kol': 'kol',
        'regular': 'regular',
      };
      const userType = intentToUserType[onboardingIntent];
      if (userType) {
        setPreselectedUserType(userType);
      }
      // Clear the intent after reading (will be used once)
      localStorage.removeItem('onboarding_intent');
    }
  }, []);

  // Check localStorage for manager referral on mount
  useEffect(() => {
    const storedReferral = getStoredManagerReferral();
    if (storedReferral) {
      setManagerRefCode(storedReferral.refCode);
      // Manager referral always preselects KOL (overrides other intents)
      setPreselectedUserType('kol');

      // Fetch manager info
      const fetchManager = async (): Promise<void> => {
        try {
          const manager = await getKolManagerByRefCode(storedReferral.refCode);
          setReferringManager(manager);
        } catch (error) {
        }
      };
      fetchManager();
    }
  }, []);

  // Ref to track if we've already initiated a redirect to prevent multiple attempts
  const hasRedirectedRef = useRef(false);

  // Ref to track user-initiated navigation (e.g., after selecting user type)
  // This prevents showing loading spinner during active navigation which causes flashing
  const isUserNavigatingRef = useRef(false);

  // Calculate correct step from Supabase data (single source of truth)
  // Memoize to prevent infinite re-calculations when profile object reference changes
  const correctStep = useMemo(() => {
    return calculateCurrentStep(profile as Partial<Profile> | null);
  }, [
    profile?.user_type,
    profile?.onboarding_completed,
    profile?.x_verified,
    profile?.id // Only recalculate when these specific fields change
  ]);
  const currentUrlStep = search.step ? parseInt(search.step) : 1;
  const isCompleted = search.completed === 'true';

  // Reset redirect ref when the correct step actually changes (not just profile data)
  const prevCorrectStepRef = useRef<number | 'complete' | null>(null);
  const prevUrlStepRef = useRef<number>(currentUrlStep);
  useEffect(() => {
    // Only reset redirect flag when the calculated step actually changes
    if (prevCorrectStepRef.current !== correctStep) {
      hasRedirectedRef.current = false;
      prevCorrectStepRef.current = correctStep;
    }
  }, [correctStep]);

  // Reset user navigating flag when URL step changes (navigation completed)
  useEffect(() => {
    if (prevUrlStepRef.current !== currentUrlStep) {
      isUserNavigatingRef.current = false;
      prevUrlStepRef.current = currentUrlStep;
    }
  }, [currentUrlStep]);

  // Check if we need to redirect (step mismatch)
  const needsRedirect = profile && !loading &&
    typeof correctStep === 'number' &&
    correctStep !== currentUrlStep;

  // Debug only when there's a mismatch
  if (needsRedirect) {
  }

  // Show loading while checking auth OR while redirecting (for existing profiles)
  // NOTE: For NEW users, profile is null - they should see UserTypeSelection, not loading
  // Only show loading for needsRedirect if profile exists (step sync for existing users)
  // Also wait for effectiveUserId to be available (needed for profile updates)
  // IMPORTANT: Don't show loading during user-initiated navigation (prevents flashing)
  const shouldShowLoading = loading || !effectiveUserId || (profile && needsRedirect && !isUserNavigatingRef.current);

  // Simple state for current step rendering
  const currentStep = isCompleted ? 'completed' :
    currentUrlStep === 1 ? 'user_type_selection' :
    currentUrlStep === 2 ? 'x_verification' : 'profile_form';

  // KOL and project ticket hooks are imported dynamically only when needed

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate({ to: '/' });
      return;
    }
  }, [isAuthenticated, loading, navigate]);

  // Simple navigation logic with debouncing to prevent loops
  // Note: Depend on specific profile fields, not the entire profile object,
  // to prevent re-runs when profile object reference changes
  useEffect(() => {
    // Skip if user is actively navigating (prevents double navigation with handleUserTypeSelect)
    if (!profile || loading || !user?.id || isUserNavigatingRef.current) return;

    // Debounce redirects to prevent loops from rapid useEffect calls
    const redirectTimer = setTimeout(() => {
      // Handle completed state - let ProjectCreatorForm handle its own navigation
      if (correctStep === 'complete' && profile.onboarding_completed) {
        if (profile.user_type === 'project_creator') {
          // DO NOT navigate here - ProjectCreatorForm handles navigation to /project-submitted
          return;
        } else if (profile.user_type === 'regular') {
          if (!hasRedirectedRef.current) {
            hasRedirectedRef.current = true;
            const redirectTo = getPostOnboardingRedirect();
            navigate({
              to: redirectTo,
              replace: true
            });
          }
          return;
        } else {
          // DO NOT navigate here - KOLProfileForm handles navigation to /dashboard
          return;
        }
      }

      // Handle step mismatch - only redirect if we haven't already done so
      if (typeof correctStep === 'number' &&
          correctStep !== currentUrlStep &&
          !hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        navigate({
          to: '/onboarding',
          search: { step: correctStep.toString() },
          replace: true
        });
      }
    }, 50); // Reduced debounce time since callback loops are fixed

    return () => clearTimeout(redirectTimer);
  }, [
    // Use specific profile fields instead of entire object to prevent unnecessary re-runs
    profile?.user_type,
    profile?.onboarding_completed,
    loading,
    user?.id,
    currentUrlStep,
    correctStep,
    navigate
  ]);

  // Handle X verification callback and edge cases
  useEffect(() => {
    if (!user?.id) return;

    // Check for X linking edge cases
    if (search.x_edge_case) {
      const edgeCase = localStorage.getItem('x_linking_edge_case');
      if (edgeCase) {
        try {
          const parsedEdgeCase = JSON.parse(edgeCase);
          setEdgeCaseData(parsedEdgeCase);
          setShowEdgeCaseDialog(true);
          localStorage.removeItem('x_linking_edge_case');
        } catch (error) {
        }
      }
      // Clean up URL
      navigate({ to: '/onboarding', replace: true });
      return;
    }

    // Check if returning from X verification callback
    if (search.step === 'x_verified') {

      // Force an immediate profile refresh
      const handleXVerificationReturn = async () => {
        setIsRefreshingProfile(true);

        try {

          // Directly fetch the latest profile from database
          const { data: latestProfile, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('dynamic_user_id', user.id)
            .single();


          if (!error && latestProfile) {

            // If X is verified in database, update local state
            if (latestProfile.x_verified) {

              // Force update via mutation to ensure cache is updated
              await updateProfile.mutateAsync({
                x_verified: true,
                twitter_handle: latestProfile.twitter_handle
              });

            } else {
            }
          } else {
          }

          // Clean up URL after ensuring profile is updated
          setTimeout(() => {
            setIsRefreshingProfile(false);
            navigate({ to: '/onboarding', replace: true });
          }, 100);
        } catch (error) {
          setIsRefreshingProfile(false);
          // Still clean up URL even on error
          navigate({ to: '/onboarding', replace: true });
        }
      };

      handleXVerificationReturn();
      return;
    }
  }, [user?.id, search.x_edge_case, search.step]); // Removed navigate from dependencies

  /**
   * Handle X linking edge case - continue with Twitter account.
   */
  const handleContinueWithTwitter = async (): Promise<void> => {

    setShowEdgeCaseDialog(false);

    // The user is already signed in with Twitter at this point
    // Update database to mark X as verified
    if (user?.id) {
      await updateProfile.mutateAsync({ x_verified: true });
    }

    // Clean up edge case data
    setEdgeCaseData(null);
    localStorage.removeItem('x_linking_state');
  };

  /**
   * Handle X linking edge case - return to original account.
   */
  const handleReturnToOriginal = async (): Promise<void> => {

    setShowEdgeCaseDialog(false);

    // Sign out current session (uses Dynamic.xyz signOut from useEnhancedAuth)
    await signOut();

    // Clear edge case data
    setEdgeCaseData(null);
    localStorage.removeItem('x_linking_state');

    // Redirect to home page so user can sign in with original account
    navigate({ to: '/' });
  };

  /**
   * Handle edge case dialog cancel.
   */
  const handleEdgeCaseCancel = (): void => {
    setShowEdgeCaseDialog(false);
    setEdgeCaseData(null);
    // Stay on current page, user can try again or skip
  };

  /**
   * Handle user type selection.
   * Memoized to prevent auto-submit timeout from being cleared on re-renders.
   */
  const handleUserTypeSelect = useCallback(async (userType: Profile['user_type']): Promise<void> => {
    // Use effectiveUserId which works for both Dynamic and legacy users
    if (!effectiveUserId) {
      console.warn('[ONBOARDING] No user ID available for user type selection');
      return;
    }

    // Mark that we're navigating to prevent loading spinner flash
    isUserNavigatingRef.current = true;

    const updates: Record<string, unknown> = {
      user_type: userType,
    };

    // Regular users complete onboarding immediately (no form needed)
    // KOLs and Project Creators must fill their forms first
    if (userType === 'regular') {
      updates.onboarding_completed = true;
    }

    // Check if user has linked Twitter identity - auto-verify X for all user types
    const hasTwitterLinked = !!getTwitterHandle();
    if (hasTwitterLinked || profile?.x_verified) {
      updates.x_verified = true;
      updates.twitter_handle = getTwitterHandle() || profile?.twitter_handle;
    }

    try {
      await updateProfile.mutateAsync(updates);
    } catch (error) {
      // Reset navigation flag on error
      isUserNavigatingRef.current = false;

      const errorMessage = error instanceof Error ? error.message : 'Failed to save your selection';
      const isTwitterConflict = errorMessage.includes('unique_twitter_handle');
      toast({
        title: isTwitterConflict ? 'X Account Already Linked' : 'Profile Update Failed',
        description: isTwitterConflict
          ? 'This X account is already connected to another Onicorn account. Please use a different X account or contact support.'
          : errorMessage,
        variant: 'destructive',
      });
      return; // Don't navigate on error
    }

    // Navigate based on user type and verification status
    if (userType === 'regular') {
      // Regular users: redirect immediately to stored URL or dashboard
      const redirectTo = getPostOnboardingRedirect();
      navigate({
        to: redirectTo,
        replace: true
      });
    } else {
      // KOLs/Project Creators: navigate to appropriate step
      // Check if X is already verified (from Twitter identity or previous verification)
      const isXVerified = hasTwitterLinked || profile?.x_verified;
      if (isXVerified) {
        // Skip X verification, go directly to profile form (step 3)
        navigate({
          to: '/onboarding',
          search: { step: '3' },
          replace: true
        });
      } else {
        // Go to X verification step (step 2)
        navigate({
          to: '/onboarding',
          search: { step: '2' },
          replace: true
        });
      }
    }
  }, [effectiveUserId, getTwitterHandle, profile?.x_verified, profile?.twitter_handle, updateProfile, navigate, toast]);

  /**
   * Handle "change account type" — the onboarding escape hatch.
   *
   * Lets a user who is stuck in a later step (e.g. an investor who was routed
   * into the KOL Application Form via a referral/CTA preselection) return to the
   * account-type selection and choose again. Clears any stored referral/intent
   * that would re-trigger a preselection, resets the persisted `user_type` to
   * null (so the step calculator routes back to step 1), and navigates there.
   * From step 1 an investor can pick "Investor" and reach the dashboard.
   */
  const handleChangeAccountType = useCallback(async (): Promise<void> => {
    if (!effectiveUserId) {
      return;
    }

    // Mark that we're navigating to prevent the loading spinner from flashing.
    isUserNavigatingRef.current = true;

    // Clear anything that would re-preselect a non-investor type on return.
    setPreselectedUserType(undefined);
    setManagerRefCode(null);
    setReferringManager(null);
    clearManagerReferral();
    localStorage.removeItem('onboarding_intent');

    try {
      // Reset the persisted type so calculateCurrentStep routes back to step 1.
      await updateProfile.mutateAsync({ user_type: null });
      // Await an auth-profile refresh so the context reflects user_type=null
      // BEFORE we navigate. This prevents the step-sync effect from briefly
      // bouncing back to the KOL/X step on a stale (cached) profile.
      await refreshProfile();
    } catch (error) {
      isUserNavigatingRef.current = false;
      toast({
        title: 'Could not change account type',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
      return;
    }

    navigate({ to: '/onboarding', search: getStepSearchParams(1), replace: true });
  }, [effectiveUserId, updateProfile, refreshProfile, navigate, toast]);


  /**
   * Handle KOL form submission.
   *
   * Note: KOLProfileForm handles the main submission logic including:
   * - KOL application creation
   * - Profile update with onboarding_completed: true
   * - Navigation to /dashboard
   *
   * This handler is only for any onboarding-specific logic if needed.
   */
  const handleKOLSubmit = async (_data: KOLProfileFormData): Promise<void> => {
    // KOLProfileForm handles all the main logic:
    // - Creating KOL application
    // - Updating profile with onboarding_completed: true
    // - Navigation to /dashboard

    // Clear manager referral from localStorage after successful KOL onboarding
    clearManagerReferral();
  };

  /**
   * Handle project creator form submission.
   *
   * Note: ProjectCreatorForm handles the main submission logic including:
   * - Project application creation
   * - Profile update with onboarding_completed: true
   * - Navigation to /project-submitted
   *
   * This handler is only for any onboarding-specific logic if needed.
   */
  const handleProjectCreatorSubmit = async (_data: ProjectTicketFormData): Promise<void> => {

    // ProjectCreatorForm handles all the main logic:
    // - Creating project application
    // - Updating profile with onboarding_completed: true
    // - Granting access to submission page
    // - Navigation to /project-submitted

    // Any onboarding-specific logic can be added here if needed in the future
  };

  // Show loading while checking authentication, restoring state, or during step mismatch
  if (shouldShowLoading || isRefreshingProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">
            {isRefreshingProfile
              ? 'Verifying your X account...'
              : needsRedirect
                ? 'Loading your onboarding step...'
                : profile?.onboarding_step
                  ? 'Restoring your progress...'
                  : 'Loading...'
            }
          </p>
        </div>
      </div>
    );
  }

  // Don't render content if not authenticated (will redirect)
  if (!isAuthenticated) {
    return <div></div>;
  }


  return (
    <div className="relative min-h-screen bg-background">
      {/* Background Shape - show across all onboarding steps */}
      <BackgroundShape />

      {/* X Linking Edge Case Dialog */}
      <XLinkingEdgeCaseDialog
        isOpen={showEdgeCaseDialog}
        edgeCase={edgeCaseData}
        onContinueWithTwitter={handleContinueWithTwitter}
        onReturnToOriginal={handleReturnToOriginal}
        onCancel={handleEdgeCaseCancel}
      />


      {/* Step Content - ALL components always rendered to maintain consistent hook order */}
      <div className="relative z-10 py-8">
        {/* Escape hatch: let users stuck past step 1 return to account-type selection */}
        {(currentStep === 'x_verification' || currentStep === 'profile_form') && (
          <div className="w-full max-w-[890px] mx-auto px-4 md:px-0 mb-2">
            <button
              type="button"
              onClick={handleChangeAccountType}
              disabled={updateProfile.isPending}
              className="text-sm text-onicorn-gray-400 underline transition-colors hover:text-onicorn-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              ← Choose a different account type
            </button>
          </div>
        )}

        {/* User Type Selection Step */}
        <div className={currentStep === 'user_type_selection' ? 'block' : 'hidden'}>
          <UserTypeSelection
            onSelect={handleUserTypeSelect}
            preselectedType={preselectedUserType}
          />
        </div>

        {/* X Verification Step */}
        <div className={currentStep === 'x_verification' ? 'block' : 'hidden'}>
          <XConnectionStep
            userType={profile?.user_type}
            onSuccess={async () => {
              if (user?.id) {
                await updateProfile.mutateAsync({ x_verified: true });
              }
            }}
            onSkip={() => {
              navigate({ to: '/onboarding', search: getStepSearchParams(3) });
            }}
          />
        </div>

        {/* KOL Profile Form Step */}
        <div className={currentStep === 'profile_form' && profile?.user_type === 'kol' ? 'block' : 'hidden'}>
          <KOLProfileForm
            twitterHandle={getTwitterHandle() || profile?.twitter_handle || undefined}
            googleEmail={getGoogleEmail() || undefined}
            initialData={undefined}
            onSubmit={handleKOLSubmit}
            managerRefCode={managerRefCode ?? referringManager?.custom_ref_code}
            managerName={referringManager?.display_name}
          />
        </div>

        {/* Project Creator Form Step */}
        <div className={currentStep === 'profile_form' && profile?.user_type === 'project_creator' ? 'block' : 'hidden'}>
          <ProjectCreatorForm
            twitterHandle={getTwitterHandle() || profile?.twitter_handle || undefined}
            initialData={undefined}
            onSubmit={handleProjectCreatorSubmit}
          />
        </div>

        {/* Invalid User Type (fallback) */}
        <div className={currentStep === 'profile_form' && profile?.user_type !== 'kol' && profile?.user_type !== 'project_creator' ? 'block' : 'hidden'}>
          <div>Invalid user type</div>
        </div>

        {/* Onboarding Success Step */}
        <div className={currentStep === 'completed' ? 'block' : 'hidden'}>
          <OnboardingSuccess
            userType={profile?.user_type || 'regular'}
            onEditTicket={
              profile?.user_type === 'project_creator'
                ? () => navigate({ to: '/onboarding/edit' })
                : undefined
            }
          />
        </div>

        {/* Unknown Step (fallback) */}
        <div className={!['user_type_selection', 'x_verification', 'profile_form', 'completed'].includes(currentStep) ? 'block' : 'hidden'}>
          <div>Unknown step</div>
        </div>
      </div>

      {/* Debug Info (development only) */}
      {/*{process.env.NODE_ENV === 'development' && (*/}
      {/*  <div className="fixed bottom-4 left-4 max-w-md space-y-2 z-50">*/}
      {/*    <div className="p-4 bg-blue-900/90 text-white text-xs rounded-lg">*/}
      {/*      <div className="font-bold text-blue-300 mb-2">🔧 Onboarding Debug:</div>*/}
      {/*      <div className="space-y-1 mb-3">*/}
      {/*        <div>Current User: {user?.email || 'None'}</div>*/}
      {/*        <div>Current Step: {currentStep}</div>*/}
      {/*        <div>User Type: {profile?.user_type || 'None'}</div>*/}
      {/*        <div>X Verified: {profile?.x_verified ? '✅' : '❌'}</div>*/}
      {/*        <div>Onboarding Complete: {profile?.onboarding_completed ? '✅' : '❌'}</div>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*)}*/}
    </div>
  );
}

/**
 * Onboarding route configuration.
 *
 * No beforeLoad is used - authentication is handled by the component
 * via useEnhancedAuth() which supports both Supabase Auth and Dynamic.xyz.
 * This allows Dynamic.xyz users (who have no Supabase session) to access onboarding.
 */
export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});
